import { useEffect } from "react";
import { styled } from "styled-components";

import { FilterButton, HeaderTitle, MainLayout, SectionTitleUnderlined } from "common/components";
import { breakpoints, mediaStyles } from "common/styles";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { getSiteLanguage } from "domains/language/selectors";
import { activateModal } from "domains/modal/actions";
import { EmergingThemes } from "domains/monitor/emergingThemes/components/EmergingThemes";
import { Filter } from "domains/monitor/filter/components/Filter";
import { getTopicsFilterSelectedIds } from "domains/monitor/filter/selectors";
import { LatestKnowledge } from "domains/monitor/latestKnowledge/components/LatestKnowledge";
import { NoAccessSidebar } from "domains/monitor/noAccess/components/NoAccessSidebar";
import { TopicSuggestions } from "domains/monitor/suggestions/components/TopicSuggestions";
import { getCanAccessMonitor, getCanFollowTopic } from "entities/permissions/selectors";
import { fetchFollowedTopics } from "entities/topics/actions";

const StyledContainer = styled.div`
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1850px;
  box-sizing: border-box;
  ${mediaStyles(({ spacing }) => `padding: 10px 0 ${spacing}rem 0;`)};

  @media ${breakpoints.medium} {
    flex-direction: row;
  }
  @media ${breakpoints.extraLarge} {
    padding-top: 3rem;
  }
`;

const StyledHeaderContainer = styled.div`
  width: 100%;
  padding: 1rem 0;
  display: flex;
  width: 100%;
  justify-content: space-between;

  ${mediaStyles(
    ({ spacing }) => `
      padding: 0 ${spacing}rem;
      margin-top: ${spacing}rem;
    `,
  )};
`;

const SkipToLatestKnowledge = styled.a`
  height: fit-content;
  opacity: 0;
  padding: 2px;
  position: absolute;
  width: fit-content;
  z-index: -1;

  &:focus-visible {
    opacity: 1;
    position: relative;
  }
`;

export const Monitor = () => {
  const dispatch = useDispatch();
  const hasAccessToMonitor = useSelector(getCanAccessMonitor);
  const canFollowTopic = useSelector(getCanFollowTopic);
  const selectedTopics = useSelector(getTopicsFilterSelectedIds);
  const track = useTrack();
  const translate = useTranslate();
  const siteLanguage = useSelector(getSiteLanguage);

  useEffect(() => {
    if (!canFollowTopic) return;

    dispatch(fetchFollowedTopics({ limit: 48, offset: 0 }));
    // Refetch followed topics when siteLanguage changes
  }, [canFollowTopic, dispatch, siteLanguage]);

  const onFilterClick = () =>
    dispatch(
      activateModal({
        id: "MonitorFilter",
      }),
    );

  const sidePanel = hasAccessToMonitor ? (
    <>
      <Filter />
      <TopicSuggestions />
    </>
  ) : (
    <>
      <SectionTitleUnderlined>{translate("mp.1.1.title")}</SectionTitleUnderlined>
      <NoAccessSidebar trackPage="Monitor" />
    </>
  );

  const headerElement = (
    <StyledHeaderContainer>
      <HeaderTitle>{translate("app.monitor.title")}</HeaderTitle>
      {hasAccessToMonitor && <FilterButton onClick={onFilterClick} count={selectedTopics.length} />}
    </StyledHeaderContainer>
  );

  useEffect(() => track({ name: "Browse page", page: "Monitor" }), [track]);

  return (
    <StyledContainer>
      <MainLayout headerElement={headerElement} sidePanel={sidePanel}>
        <SkipToLatestKnowledge href="#latest-knowledge">
          {translate("app.monitor.skip_to_latest_knowledge")}
        </SkipToLatestKnowledge>
        <StyledContent>
          <EmergingThemes />
          <LatestKnowledge />
        </StyledContent>
      </MainLayout>
    </StyledContainer>
  );
};
